import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

export class SmartPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: ({ nativeEvent: event }: React.PointerEvent) => {
        const target = event.target as HTMLElement | null;
        if (!target) return false;

        if (
          target.closest('button') ||
          target.closest('a') ||
          target.closest('input') ||
          target.closest('[data-no-drag="true"]')
        ) {
          return false;
        }

        return true;
      },
    },
  ];
}
import { RefreshCw, Plus, CheckSquare, Square, Sparkles, Users, TrendingUp, Target, Clock } from 'lucide-react';

import {
  listLeadsAPI,
  getLeadStatsAPI,
  updateLeadStageAPI,
  bulkStageUpdateAPI,
} from '../../../../Redux/ApiCalls/Dashboard/LeadsAPI';
import { useAppSelector } from '../../../../Redux/Hooks';
import {
  STAGE_ORDER,
  STAGE_LABELS,
  VALID_TRANSITIONS,
  EVENT_TYPE_LABELS,
  SOURCE_LABELS,
} from '../../../../Data/leads.dto';
import type {
  LeadCard,
  LeadStage,
  LeadStats,
  AIPriority,
} from '../../../../Data/leads.dto';
import { showErrorToast, showSuccessToast } from '../../../atoms/Utlis/Toast';
import { usePermissions } from '../../../../hooks/usePermissions';
import PermissionGate from '../../../atoms/Utlis/PermissionGate';
import AddLeadModal from './AddLeadModal';
import MarkLostModal from './MarkLostModal';
import DraggableCard from './DraggableCard';
import DroppableColumn from './DroppableColumn';


import {
  formatPipelineValue,
  COLUMN_VALUES,
} from './leadHelpers';


import { useDocumentSharing } from './useDocumentSharing';
import { NoDocumentModal } from './NoDocumentModal';
import { VerifyDocumentModal } from './VerifyDocumentModal';
import { SelectDocumentModal } from './SelectDocumentModal';
import { DocumentPrintTemplate } from '../DocumentPrintTemplate';




const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-shrink: 0;
  box-sizing: border-box;
  overflow: visible;
  background:
    radial-gradient(ellipse 70% 40% at 0% 0%, rgba(201, 77, 170, 0.05), transparent 55%),
    radial-gradient(ellipse 50% 35% at 100% 0%, rgba(255, 201, 81, 0.06), transparent 50%),
    #f8fafc;
  font-family: 'Urbanist', sans-serif;
  padding-bottom: 32px;
`;

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  margin: 20px 24px 0;
  padding: 22px 24px;
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.18) 0%,
    rgba(206, 86, 176, 0.14) 55%,
    rgba(124, 77, 255, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  flex-shrink: 0;
  animation: ${fadeUp} 0.35s ease-out;
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -50%;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.35) 0%, transparent 70%);
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  font-family: 'Urbanist', sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: #111827;
  margin: 0;
  line-height: 1.1;
`;

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 10px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #374151;
  transition: all 0.15s;
  font-family: 'Urbanist', sans-serif;
  &:hover {
    background: #fff;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }
`;

const AddLeadBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 11px 18px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.95) 0%, rgba(206, 86, 176, 0.95) 100%);
  color: white;
  font-family: 'Urbanist', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.24);
  transition: all 0.18s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(201, 77, 170, 0.32);
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  padding: 16px 24px 0;
  flex-shrink: 0;
  animation: ${fadeUp} 0.4s ease-out 0.05s both;
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ $tone?: string }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.04);
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`;

const StatBody = styled.div`
  min-width: 0;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.55);
  margin-top: 3px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px 0;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const FilterTab = styled.button<{ active?: boolean }>`
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.active ? 'transparent' : 'rgba(0, 0, 0, 0.06)')};
  background: ${(p) =>
    p.active
      ? 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.95) 0%, rgba(206, 86, 176, 0.95) 100%)'
      : '#fff'};
  color: ${(p) => (p.active ? 'white' : '#4b5563')};
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  font-weight: ${(p) => (p.active ? 700 : 600)};
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: ${(p) =>
    p.active ? '0 6px 16px rgba(201, 77, 170, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.03)'};
  &:hover {
    transform: translateY(-1px);
    border-color: rgba(201, 77, 170, 0.2);
  }
`;

const StatsBar = styled.div`
  padding: 10px 24px 12px;
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const StaleChip = styled.button<{ visible: boolean }>`
  display: ${(p) => (p.visible ? 'inline-flex' : 'none')};
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #fde68a;
  background: #fef3c7;
  color: #92400e;
  font-family: 'Urbanist', sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #fde68a;
  }
`;

const BoardWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
  overflow: visible;
  padding: 8px 24px 24px;
`;

const ColHeader = styled.div`
  padding: 2px 4px 10px;
  flex-shrink: 0;
`;

const ColTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColName = styled.span`
  font-family: 'Urbanist', sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #111827;
`;

const ColCount = styled.span`
  font-family: 'Urbanist', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.1);
  padding: 3px 8px;
  border-radius: 999px;
`;

const ColValue = styled.p`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  margin: 4px 0 0;
`;

const CardList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  overflow-x: auto;
  overflow-y: visible;
  padding: 2px 4px 10px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar { height: 5px; }
  &::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
`;

const EmptyCol = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 88px;
  border-radius: 14px;
  border: 1px dashed rgba(0, 0, 0, 0.08);
  background: rgba(248, 250, 252, 0.8);
`;

const EmptyText = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #d1d5db;
`;

const LoadMoreBtn = styled.button`
  width: 100%;
  padding: 10px 12px;
  margin-top: 8px;
  background: #f8fafc;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  font-family: 'Urbanist', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.18s ease;
  &:hover:not(:disabled) {
    background: rgba(201, 77, 170, 0.06);
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.18);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  gap: 12px;
  min-height: 280px;
  padding: 48px 24px;
`;

const LoadingText = styled.p`
  font-family: 'Urbanist', sans-serif;
  color: #9ca3af;
  font-size: 15px;
  font-weight: 600;
  margin: 0;
`;

const CardWrap = styled.div`
  background: white;
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(201, 77, 170, 0.15);
  opacity: 0.95;
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 6px;
`;

const CardName = styled.span`
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
`;

const PriorityBadge = styled.span<{ priority: AIPriority }>`
  font-family: 'Urbanist', sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  background: #708090;
  flex-shrink: 0;
`;

const CardMeta = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 11px;
  color: #6b7280;
  margin: 4px 0 0;
  line-height: 1.4;
`;

const BulkBar = styled.div<{ visible: boolean }>`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(${(p) => (p.visible ? '0' : '80px')});
  background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
  color: white;
  border-radius: 16px;
  padding: 14px 22px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.08);
  z-index: 100;
  transition: transform 0.25s ease;
  font-family: 'Urbanist', sans-serif;
  font-size: 14px;
`;

const BulkSelect = styled.select`
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.08);
  color: white;
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  cursor: pointer;
`;

const BulkApply = styled.button`
  padding: 8px 18px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.95) 0%, rgba(206, 86, 176, 0.95) 100%);
  color: white;
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const BulkCancel = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  cursor: pointer;
  &:hover { color: white; }
`;




const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'P1', label: 'P1' },
  { key: 'P2', label: 'P2' },
  { key: 'P3', label: 'P3' },
  { key: 'wedding', label: 'Wedding' },
  { key: 'maternity', label: 'Maternity' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'form', label: 'Form' },
];

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
const LeadsDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission("leads.update");

  // ── State ─────────────────────────────────────────────────
  const [kanban, setKanban] = useState<Map<LeadStage, LeadCard[]>>(new Map());
  const [stats, setStats] = useState<LeadStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [staleFilter, setStaleFilter] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);

  // DnD state
  const [draggingLead, setDraggingLead] = useState<LeadCard | null>(null);
  const [validTargets, setValidTargets] = useState<LeadStage[]>([]);

  const sensors = useSensors(
    useSensor(SmartPointerSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
      bypassActivationConstraint({ event }) {
        const target = event.target as HTMLElement | null;
        if (target && target.closest('[data-drag-handle="true"]')) {
          return true;
        }
        return false;
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Auth User
  const { user, studio_logo, business } = useAppSelector((state: any) => state.auth);

  // Mark lost modal state
  const [markLostModal, setMarkLostModal] = useState<{ open: boolean; lead: LeadCard | null; targetStage?: LeadStage }>({ open: false, lead: null });
  const [markLostLoading, setMarkLostLoading] = useState(false);

  // Bulk actions state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkTargetStage, setBulkTargetStage] = useState<LeadStage>('CONTACTED');
  const [bulkLoading, setBulkLoading] = useState(false);

  // Kanban column pagination state
  const [stagePages, setStagePages] = useState<Map<LeadStage, number>>(new Map(STAGE_ORDER.map(s => [s, 1])));
  const [stageHasMore, setStageHasMore] = useState<Map<LeadStage, boolean>>(new Map(STAGE_ORDER.map(s => [s, false])));
  const [columnLoading, setColumnLoading] = useState<Map<LeadStage, boolean>>(new Map());

  // ── Fetching Data ──────────────────────────────────────────
  const fetchColumn = async (stage: LeadStage, page = 1): Promise<{ results: LeadCard[]; hasMore: boolean }> => {
    try {
      const res = await listLeadsAPI({ stage, page, page_size: 50 });
      const total = res.count || 0;
      const fetchedSoFar = page * 50;
      return {
        results: res.results || [],
        hasMore: total > fetchedSoFar,
      };
    } catch {
      return { results: [], hasMore: false };
    }
  };

  const loadMore = async (stage: LeadStage) => {
    const nextPage = (stagePages.get(stage) || 1) + 1;
    setColumnLoading(prev => {
      const next = new Map(prev);
      next.set(stage, true);
      return next;
    });
    const res = await fetchColumn(stage, nextPage);

    setKanban(prev => {
      const next = new Map(prev);
      const currentLeads = prev.get(stage) || [];
      const existingIds = new Set(currentLeads.map(l => l.id));
      const filteredNew = res.results.filter(l => !existingIds.has(l.id));
      next.set(stage, [...currentLeads, ...filteredNew]);
      return next;
    });

    setStagePages(prev => {
      const next = new Map(prev);
      next.set(stage, nextPage);
      return next;
    });

    setStageHasMore(prev => {
      const next = new Map(prev);
      next.set(stage, res.hasMore);
      return next;
    });

    setColumnLoading(prev => {
      const next = new Map(prev);
      next.set(stage, false);
      return next;
    });
  };

  const fetchAllColumns = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const statsData = await getLeadStatsAPI().catch(() => null);

      const results = await Promise.all(
        STAGE_ORDER.map(async (stage) => {
          const res = await fetchColumn(stage, 1);
          return { stage, ...res };
        })
      );

      setKanban(() => {
        const map = new Map<LeadStage, LeadCard[]>();
        results.forEach(r => map.set(r.stage, r.results));
        return map;
      });

      setStagePages(() => {
        const map = new Map<LeadStage, number>();
        STAGE_ORDER.forEach(s => map.set(s, 1));
        return map;
      });

      setStageHasMore(() => {
        const map = new Map<LeadStage, boolean>();
        results.forEach(r => map.set(r.stage, r.hasMore));
        return map;
      });

      if (statsData) setStats(statsData);
      setLastUpdated(new Date());
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllColumns(false);
  }, [fetchAllColumns]);

  // Document Sharing Logic (Hook)
  const {
    sharingLeadId,
    activeQuotation,
    activeOrder,
    tempQuotation,
    tempOrder,
    quotationsSelectionList,
    ordersSelectionList,
    noQuotationModal,
    setNoQuotationModal,
    noOrderModal,
    setNoOrderModal,
    showQuotationSelectModal,
    setShowQuotationSelectModal,
    showOrderSelectModal,
    setShowOrderSelectModal,
    showQuotationVerifyModal,
    setShowQuotationVerifyModal,
    showOrderVerifyModal,
    setShowOrderVerifyModal,
    handleSendQuotation,
    handleUpdateQuotation,
    handleSendOrder,
    handleConfirmSendQuotation,
    handleCancelSendQuotation,
    handleConfirmSendOrder,
    handleCancelSendOrder,
    handleSelectQuotation,
    handleSelectOrder,
    setTempQuotation,
    setTempOrder,
    setSharingLeadId,
    printRef,
    orderPrintRef,
  } = useDocumentSharing(() => fetchAllColumns(false));

  // ── Filters ────────────────────────────────────────────────
  const applyFilter = (leads: LeadCard[]): LeadCard[] => {
    let result = leads;
    const isLeadStale = (dateStr: string): boolean =>
      Date.now() - new Date(dateStr).getTime() > 7 * 86400000;

    if (staleFilter) result = result.filter(l => isLeadStale(l.last_activity_at));
    if (activeFilter === 'P1' || activeFilter === 'P2' || activeFilter === 'P3')
      result = result.filter(l => l.ai_priority === activeFilter);
    else if (activeFilter === 'whatsapp')
      result = result.filter(l => l.source === 'whatsapp');
    else if (activeFilter === 'form')
      result = result.filter(l => l.source === 'form');
    else if (!['all'].includes(activeFilter))
      result = result.filter(l => l.event_type === activeFilter);
    return result;
  };

  // ── Drag & Drop Handlers ──────────────────────────────────
  const handleDragStart = (e: DragStartEvent) => {
    if (!canUpdate) return;
    const all: LeadCard[] = Array.from(kanban.values()).flat();
    const lead = all.find(l => l.id === String(e.active.id));
    if (!lead) return;
    setDraggingLead(lead);
    setValidTargets(VALID_TRANSITIONS[lead.stage]);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setDraggingLead(null);
    setValidTargets([]);
    if (!e.over || !draggingLead) return;

    const targetStage = e.over.id as LeadStage;
    const originalStage = draggingLead.stage;
    if (targetStage === originalStage) return;

    if (!VALID_TRANSITIONS[originalStage].includes(targetStage)) {
      showErrorToast(`Cannot move from ${STAGE_LABELS[originalStage]} to ${STAGE_LABELS[targetStage]}`);
      return;
    }

    if (targetStage === 'LOST') {
      setMarkLostModal({ open: true, lead: draggingLead, targetStage });
      return;
    }

    // Optimistic Update
    const leadToMove = draggingLead;
    setKanban(prev => {
      const next = new Map(prev);
      next.set(originalStage, (next.get(originalStage) || []).filter(l => l.id !== leadToMove.id));
      next.set(targetStage, [{ ...leadToMove, stage: targetStage }, ...(next.get(targetStage) || [])]);
      return next;
    });

    updateLeadStageAPI(leadToMove.id, { stage: targetStage }).catch(err => {
      showErrorToast(err?.response?.data?.detail || 'Stage update failed');
      // Revert on failure
      setKanban(prev => {
        const next = new Map(prev);
        next.set(targetStage, (next.get(targetStage) || []).filter(l => l.id !== leadToMove.id));
        next.set(originalStage, [{ ...leadToMove, stage: originalStage }, ...(next.get(originalStage) || [])]);
        return next;
      });
    });
  };

  // ── Mark Lost Confirmation ──────────────────────────────
  const handleMarkLostConfirm = async (reason: string) => {
    const lead = markLostModal.lead;
    if (!lead) return;
    setMarkLostLoading(true);
    try {
      await updateLeadStageAPI(lead.id, { stage: 'LOST', lost_reason: reason });
      setKanban(prev => {
        const next = new Map(prev);
        next.set(lead.stage, (next.get(lead.stage) || []).filter(l => l.id !== lead.id));
        next.set('LOST', [{ ...lead, stage: 'LOST' }, ...(next.get('LOST') || [])]);
        return next;
      });
      showSuccessToast('Lead marked as lost');
    } catch (err: any) {
      showErrorToast(err?.response?.data?.detail || 'Failed to mark as lost');
    } finally {
      setMarkLostLoading(false);
      setMarkLostModal({ open: false, lead: null });
    }
  };

  // ── Bulk Actions ──────────────────────────────────────────
  const handleBulkApply = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await bulkStageUpdateAPI({ lead_ids: Array.from(selectedIds), stage: bulkTargetStage });
      if (res.failed?.length) {
        showSuccessToast(`${res.updated} moved, ${res.failed.length} skipped (invalid transition)`);
      } else {
        showSuccessToast(`${res.updated} leads moved to ${STAGE_LABELS[bulkTargetStage]}`);
      }
      setSelectedIds(new Set());
      setSelectMode(false);
      fetchAllColumns(true);
    } catch {
      showErrorToast('Bulk update failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleCheckToggle = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size > 0) {
          const firstId = Array.from(next)[0];
          let firstStage: LeadStage | null = null;
          let clickedStage: LeadStage | null = null;
          for (const [stage, leads] of Array.from(kanban.entries())) {
            if (leads.some((l: LeadCard) => l.id === firstId)) firstStage = stage;
            if (leads.some((l: LeadCard) => l.id === id)) clickedStage = stage;
          }
          if (firstStage && clickedStage && firstStage !== clickedStage) {
            showErrorToast('You can only select leads from the same stage');
            return prev;
          }
        }
        next.add(id);

        if (next.size === 1) {
          let clickedStage: LeadStage | null = null;
          for (const [stage, leads] of Array.from(kanban.entries())) {
            if (leads.some((l: LeadCard) => l.id === id)) clickedStage = stage;
          }
          if (clickedStage && VALID_TRANSITIONS[clickedStage]?.length > 0) {
            setBulkTargetStage(VALID_TRANSITIONS[clickedStage][0]);
          }
        }
      }
      return next;
    });
  };

  const statsText = stats
    ? `${stats.total_leads} leads · ${formatPipelineValue(stats.pipeline_value)} pipeline`
    : '';

  const secondsSince = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);

  const handleStageCheckToggle = (stage: LeadStage, stageLeads: LeadCard[]) => {
    if (stageLeads.length === 0) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      const stageLeadIds = stageLeads.map(l => l.id);

      if (next.size > 0) {
        const firstId = Array.from(next)[0];
        let firstStage: LeadStage | null = null;
        for (const [s, leads] of Array.from(kanban.entries())) {
          if (leads.some((l: LeadCard) => l.id === firstId)) firstStage = s;
        }
        if (firstStage && firstStage !== stage) {
          showErrorToast('You can only select leads from the same stage');
          return prev;
        }
      }

      const allSelected = stageLeadIds.every(id => next.has(id));
      if (allSelected) {
        stageLeadIds.forEach(id => next.delete(id));
      } else {
        stageLeadIds.forEach(id => next.add(id));
        if (VALID_TRANSITIONS[stage]?.length > 0) {
          setBulkTargetStage(VALID_TRANSITIONS[stage][0]);
        }
      }
      return next;
    });
  };

  let validBulkTargets: LeadStage[] = [];
  if (selectMode && selectedIds.size > 0) {
    const firstId = Array.from(selectedIds)[0];
    for (const [stage, leads] of Array.from(kanban.entries())) {
      if (leads.some((l: LeadCard) => l.id === firstId)) {
        validBulkTargets = VALID_TRANSITIONS[stage as LeadStage] || [];
        break;
      }
    }
  }

  return (
    <PageWrap>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={13} />
                Sales pipeline
              </HeroBadge>
              <PageTitle className='linear-text'>Leads</PageTitle>
              <PageSubtitle>Track, filter, and move leads through your pipeline</PageSubtitle>
            </div>

          </HeroText>
          <HeaderRight>
            <IconBtn onClick={() => fetchAllColumns(false)} title={`Last updated ${secondsSince}s ago`}>
              <RefreshCw size={16} />
            </IconBtn>
            {canUpdate && (
              <IconBtn
                onClick={() => { setSelectMode(m => !m); setSelectedIds(new Set()); }}
                title="Multi-select"
              >
                <CheckSquare size={16} color={selectMode ? '#C94DAA' : '#374151'} />
              </IconBtn>
            )}
            <PermissionGate permission="leads.create">
              <AddLeadBtn id="add-lead-btn" onClick={() => setAddModalOpen(true)}>
                <Plus size={15} />
                Add Lead
              </AddLeadBtn>
            </PermissionGate>
          </HeaderRight>
        </HeroContent>
      </Hero>

      {stats && (
        <StatsRow>
          <StatCard>
            <StatIcon $tone="#7c4dff"><Users size={20} /></StatIcon>
            <StatBody>
              <StatValue>{stats.total_leads}</StatValue>
              <StatLabel>Total leads</StatLabel>
            </StatBody>
          </StatCard>
          <StatCard>
            <StatIcon $tone="#c94daa"><TrendingUp size={20} /></StatIcon>
            <StatBody>
              <StatValue>{formatPipelineValue(stats.pipeline_value)}</StatValue>
              <StatLabel>Pipeline value</StatLabel>
            </StatBody>
          </StatCard>
          <StatCard>
            <StatIcon $tone="#16a34a"><Target size={20} /></StatIcon>
            <StatBody>
              <StatValue>{stats.conversion_rate || '—'}</StatValue>
              <StatLabel>Conversion rate</StatLabel>
            </StatBody>
          </StatCard>
          <StatCard>
            <StatIcon $tone="#f59e0b"><Clock size={20} /></StatIcon>
            <StatBody>
              <StatValue>{stats.stale_leads}</StatValue>
              <StatLabel>Stale leads</StatLabel>
            </StatBody>
          </StatCard>
        </StatsRow>
      )}

      <FilterRow>
        {FILTER_TABS.map(tab => (
          <FilterTab
            key={tab.key}
            id={`filter-tab-${tab.key}`}
            active={activeFilter === tab.key && !staleFilter}
            onClick={() => { setActiveFilter(tab.key); setStaleFilter(false); }}
          >
            {tab.label}
          </FilterTab>
        ))}
      </FilterRow>

      {/* <StatsBar>
        <span>{statsText}</span>
        <StaleChip
          visible={!!stats && stats.stale_leads > 0}
          onClick={() => setStaleFilter(s => !s)}
        >
          <Clock size={12} />
          {staleFilter ? 'Show all leads' : `${stats?.stale_leads ?? 0} stale leads`}
        </StaleChip>
      </StatsBar> */}

      {loading ? (
        <LoadingWrap>
          <LoadingText>Loading leads…</LoadingText>
        </LoadingWrap>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={rectIntersection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <BoardWrap>
            {STAGE_ORDER.map(stage => {
              const allLeads = kanban.get(stage) || [];
              const filtered = applyFilter(allLeads);
              const isValid = validTargets.includes(stage);
              const isDragActive = !!draggingLead;

              return (
                <DroppableColumn
                  key={stage}
                  stage={stage}
                  isValidTarget={isValid}
                  isDragActive={isDragActive}
                >
                  <ColHeader>
                    <ColTitleRow>
                      {selectMode && (
                        <div
                          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginRight: '6px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStageCheckToggle(stage, filtered);
                          }}
                        >
                          {filtered.length > 0 && filtered.every(l => selectedIds.has(l.id)) ? (
                            <CheckSquare size={16} color="#C94DAA" />
                          ) : (
                            <Square size={16} color="#d1d5db" />
                          )}
                        </div>
                      )}
                      <ColName>{STAGE_LABELS[stage]}</ColName>
                      <ColCount>{filtered.length}</ColCount>
                    </ColTitleRow>
                    <ColValue>{COLUMN_VALUES(filtered)}</ColValue>
                  </ColHeader>

                  <CardList>
                    {filtered.length === 0 ? (
                      <EmptyCol><EmptyText>No leads</EmptyText></EmptyCol>
                    ) : (
                      filtered.map(lead => (
                        <DraggableCard
                          key={lead.id}
                          lead={lead}
                          isSelected={selectedIds.has(lead.id)}
                          selectMode={selectMode}
                          onCardClick={id => navigate(`/dashboard/leads/${id}`)}
                          onCheckToggle={handleCheckToggle}
                          isDragActive={isDragActive}
                          onSendQuotation={handleSendQuotation}
                          onUpdateQuotation={handleUpdateQuotation}
                          onSendOrder={handleSendOrder}
                          isSharing={sharingLeadId === lead.id}
                        />
                      ))
                    )}
                  </CardList>
                  {stageHasMore.get(stage) && (
                    <LoadMoreBtn
                      onClick={(e) => {
                        e.stopPropagation();
                        loadMore(stage);
                      }}
                      disabled={columnLoading.get(stage)}
                    >
                      {columnLoading.get(stage) ? 'Loading...' : 'Load more'}
                    </LoadMoreBtn>
                  )}
                </DroppableColumn>
              );
            })}
          </BoardWrap>

          {/* Ghost Drag Overlay */}
          <DragOverlay>
            {draggingLead && (
              <CardWrap style={{ width: 180 }}>
                <CardTopRow>
                  <CardName>{draggingLead.name}</CardName>
                  {draggingLead.ai_priority && (
                    <PriorityBadge priority={draggingLead.ai_priority}>
                      {draggingLead.ai_priority} ★
                    </PriorityBadge>
                  )}
                </CardTopRow>
                <CardMeta>{EVENT_TYPE_LABELS[draggingLead.event_type]}</CardMeta>
              </CardWrap>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Bulk Action Bar */}
      <BulkBar visible={selectMode && selectedIds.size > 0}>
        <span>{selectedIds.size} lead{selectedIds.size !== 1 ? 's' : ''} selected · Move to:</span>
        <BulkSelect
          value={bulkTargetStage}
          onChange={e => setBulkTargetStage(e.target.value as LeadStage)}
        >
          {validBulkTargets.map(s => (
            <option key={s} value={s}>{STAGE_LABELS[s]}</option>
          ))}
        </BulkSelect>
        <BulkApply onClick={handleBulkApply} disabled={bulkLoading}>
          {bulkLoading ? 'Applying…' : 'Apply'}
        </BulkApply>
        <BulkCancel onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }}>
          Cancel
        </BulkCancel>
      </BulkBar>

      {/* Add Lead Modal */}
      <AddLeadModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => {
          setAddModalOpen(false);
          fetchAllColumns(true);
          navigate(`/dashboard/leads`);
        }}
      />

      {/* Mark Lost Modal */}
      <MarkLostModal
        open={markLostModal.open}
        onClose={() => setMarkLostModal({ open: false, lead: null })}
        onConfirm={handleMarkLostConfirm}
        loading={markLostLoading}
      />

      {/* Reusable Sharing Modals */}
      <NoDocumentModal
        open={noQuotationModal.open}
        lead={noQuotationModal.lead}
        type="quotation"
        onClose={() => setNoQuotationModal({ open: false, lead: null })}
        onConfirm={() => {
          const lead = noQuotationModal.lead;
          setNoQuotationModal({ open: false, lead: null });
          navigate('/dashboard/quotation/singleQuotation', { state: { leadData: lead } });
        }}
      />

      <NoDocumentModal
        open={noOrderModal.open}
        lead={noOrderModal.lead}
        type="order"
        onClose={() => setNoOrderModal({ open: false, lead: null })}
        onConfirm={() => {
          const lead = noOrderModal.lead;
          setNoOrderModal({ open: false, lead: null });
          navigate('/dashboard/orders/create', { state: { leadData: lead } });
        }}
      />

      <VerifyDocumentModal
        open={showQuotationVerifyModal}
        tempDoc={tempQuotation}
        type="quotation"
        onClose={handleCancelSendQuotation}
        onConfirm={handleConfirmSendQuotation}
      />

      <VerifyDocumentModal
        open={showOrderVerifyModal}
        tempDoc={tempOrder}
        type="order"
        onClose={handleCancelSendOrder}
        onConfirm={handleConfirmSendOrder}
      />

      <SelectDocumentModal
        open={showQuotationSelectModal}
        list={quotationsSelectionList}
        type="quotation"
        onClose={() => { setShowQuotationSelectModal(false); setSharingLeadId(null); }}
        onSelect={handleSelectQuotation}
      />

      <SelectDocumentModal
        open={showOrderSelectModal}
        list={ordersSelectionList}
        type="order"
        onClose={() => { setShowOrderSelectModal(false); setSharingLeadId(null); }}
        onSelect={handleSelectOrder}
      />

      {/* Offscreen Print Templates for PDF rendering */}
      <DocumentPrintTemplate
        printRef={printRef}
        activeDoc={activeQuotation}
        user={user}
        studioLogo={studio_logo || business?.studio_logo}
        studioName={business?.studio_name}
        studioAddress={business?.address}
        type="quotation"
      />

      <DocumentPrintTemplate
        printRef={orderPrintRef}
        activeDoc={activeOrder}
        user={user}
        studioLogo={studio_logo || business?.studio_logo}
        studioName={business?.studio_name}
        studioAddress={business?.address}
        type="order"
      />
    </PageWrap>
  );
};

export default LeadsDashboardPage;
